import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Card, Checkbox, Chip, Divider, Stack, Typography } from "@mui/material";
import { Eye } from "@phosphor-icons/react";
import type { InitiativeListItem } from "../../api/initiatives";
import FlexBox from "../../components/layouts/Flexbox";
import { APP_COLORS, APP_FONT_FAMILIES } from "../../enums";
import { getInitiativeTypeDisplayLabel } from "./create-initiative-drawer/config";
import { SummaryReadMorePopover } from "./SummaryReadMorePopover";

export interface InitiativeSelectableCardProps {
  initiative: InitiativeListItem;
  selected?: boolean;
  onSelectChange?: (initiative: InitiativeListItem, selected: boolean) => void;
}

export function InitiativeSelectableCard({
  initiative,
  selected = false,
  onSelectChange,
}: InitiativeSelectableCardProps) {
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectChange?.(initiative, event.target.checked);
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        cursor: onSelectChange ? "pointer" : "default",
        border: selected ? `1px solid ${APP_COLORS.primary}` : undefined,
      }}
      onClick={() => onSelectChange?.(initiative, !selected)}
    >
      <Stack flex={1} minHeight={0} overflow="hidden">
        {onSelectChange &&
          <>
            <FlexBox
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={0.5}
            >
              <FlexBox alignItems="center" gap={0.5}>
                {onSelectChange && (
                  <Checkbox
                    checked={selected}
                    onChange={handleCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0, mr: 0.5 }}
                    inputProps={{ "aria-label": `Select ${initiative.title}` }}
                  />
                )}
                {initiative.isPublic ? (
                  <Eye
                    fontSize={20}
                    color={APP_COLORS.secondary}
                    weight="bold"
                  />
                ) : (
                  <VisibilityOffOutlinedIcon
                    sx={{ fontSize: 20, color: APP_COLORS.yellow }}
                  />
                )}
                <Typography
                  variant="small-ibmPlexMono"
                  color={
                    initiative.isPublic ? APP_COLORS.secondary : APP_COLORS.yellow
                  }
                >
                  {initiative.isPublic ? "Public" : "Private"}
                </Typography>
              </FlexBox>
              <FlexBox alignItems="center" gap={0.5}>
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    bgcolor: APP_COLORS.successLight2,
                    color: APP_COLORS.successDark,
                    fontWeight: 500,
                    fontFamily: APP_FONT_FAMILIES.ibmPlexMono,
                    fontSize: "0.6875rem",
                    "& .MuiChip-label": { fontSize: "0.6875rem" },
                  }}
                />
              </FlexBox>
            </FlexBox>

            <Divider />
          </>}

        <Typography
          variant="bodyLarge-helveticaNeue-medium"
          color={APP_COLORS.text}
          fontSize="0.9375rem"
          display="-webkit-box"
          overflow={"hidden"}
          textOverflow={"ellipsis"}
          sx={{
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
          title={initiative.title}
        >
          {initiative.title}
        </Typography>


        <Typography
          variant="body-helveticaNeue"
          fontSize="0.8125rem"
          color={APP_COLORS.successDark}
        >
          {getInitiativeTypeDisplayLabel(initiative.type)}
        </Typography>

        <Stack flex={1} minHeight={0} mt={2}>
          <SummaryReadMorePopover initiative={initiative} lineClamp={3} />
        </Stack>
      </Stack>
    </Card>
  );
}
